import { Backdrop, Button, Card, Checkbox, Eyebrow, Input, Rule, ThemeToggle } from '@memorizecards/ui';
import type { FormEvent, ReactNode } from 'react';
import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { ApiError } from '../lib/api';
import { useAuth } from '../state/auth';

const SITE = 'https://memorizecards.com.br';

export function Entrar(): ReactNode {
  const { user, entrar } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [lembrar, setLembrar] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  // Para onde voltar depois de entrar, quando a sessão caiu a meio de algo.
  const destino =
    (location.state as { de?: string } | null)?.de ?? '/baralhos';

  // Vale tanto para quem acabou de submeter o formulário como para quem chegou
  // aqui já com sessão. Redirecionar a partir do render, e não de dentro do
  // `submit`, evita a corrida em que o `<Navigate>` do render seguinte à
  // gravação do utilizador se antecipava ao `navigate()` e engolia o desvio
  // para a troca de senha temporária.
  if (user) {
    return <Navigate to={user.mustChangePassword ? '/trocar-senha' : destino} replace />;
  }

  async function aoSubmeter(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);

    try {
      await entrar({ email: email.trim(), senha, lembrar });
    } catch (error) {
      setErro(
        error instanceof ApiError
          ? error.message
          : 'Não consegui entrar agora. Tente de novo.',
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="relative grid min-h-screen place-items-center px-4 py-12">
      <Backdrop />
      <div className="fixed right-4 top-4 z-50">
        <ThemeToggle size="sm" />
      </div>

      <div className="relative z-[2] w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          <Logo />
          <Eyebrow>Área do aluno</Eyebrow>
          <h1 className="text-mc-h3">Entre para estudar</h1>
        </div>

        <Card padding="lg">
          <form onSubmit={aoSubmeter} noValidate className="flex flex-col gap-5">
            <Input
              label="E-mail"
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@exemplo.com"
            />

            <Input
              label="Senha"
              type="password"
              name="senha"
              autoComplete="current-password"
              required
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="••••••••"
            />

            <Checkbox
              label="Continuar conectado neste aparelho"
              checked={lembrar}
              onChange={(e) => setLembrar(e.target.checked)}
            />

            {erro ? (
              <p
                className="rounded-mc-field border border-mc-danger/40 bg-mc-danger/10 px-4 py-3 text-sm text-mc-ink"
                role="alert"
              >
                {erro}
              </p>
            ) : null}

            <Button type="submit" size="lg" fullWidth loading={enviando}>
              Entrar
            </Button>
          </form>

          <Rule className="my-6" />

          <div className="flex flex-col gap-3 text-center text-sm">
            <Link
              to="/esqueci-senha"
              className="text-mc-ink2 underline underline-offset-4 hover:text-mc-accent"
            >
              Esqueci minha senha
            </Link>
            <p className="text-mc-ink3">
              Ainda não tem acesso?{' '}
              <a
                href={`${SITE}/planos`}
                className="text-mc-accent underline underline-offset-4"
              >
                Ver planos
              </a>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
